import React, {
  useContext, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';

import Variations from '@components/shared/ProductCard/Variations';
import formatCost from '@lib/formatCost';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import If from '@components/shared/conditional/If/If';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import UserContext from '@context/User/user-context';
import { useLazyQuery } from '@apollo/client';
import CART_QUERY from '@graphql/User/queries/get-cart-for-user.graphql';
import SaleBanner from '@components/shared/shop/ShopProductCard/SaleBanner/SaleBanner';
import { useTheme } from '@components/theme';
import classnames from 'classnames';
import useAddToCart, { ADDED_TO_CART } from '@hooks/User/useAddToCart';
import SaveProductForLater
  from '@components/pages/ProductV2/shared/components/SaveProductForLater/SaveProductForLater';
import DesignAwardBadge from '@components/shared/DesignAwards/DesignAwardBadge';
import Image from '@components/shared/Image/Image';
import breakpoints from '@constants/breakpoints';
import Skeleton from '../Skeleton/Skeleton';
import styles from './ProductCard.module.scss';

ProductCard.propTypes = {
  className: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  saved: PropTypes.bool,
  setQuickViewModalContent: PropTypes.func,
  vendorVariant: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    title: PropTypes.string.isRequired,
    productUrl: PropTypes.string.isRequired,
    currentPrice: PropTypes.number,
    retailPrice: PropTypes.shape({
      price: PropTypes.number,
    }).isRequired,
    image: PropTypes.shape({
      uri: PropTypes.string.isRequired,
    }).isRequired,
    vendor: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

ProductCard.defaultProps = {
  className: '',
  readOnly: false,
  saved: false,
};

export default function ProductCard(props) {
  const {
    className,
    onSave,
    readOnly,
    saved,
    setQuickViewModalContent,
    vendorVariant,
  } = props;
  const {
    image,
    title,
    retailPrice,
    currentPrice,
    isOnSale,
    vendor,
    productUrl,
    groupCount,
    productGroupUrl,
    designAward
  } = vendorVariant;

  const theme = useTheme();

  const { user } = useContext(UserContext);

  const [getUserCart] = useLazyQuery(CART_QUERY);
  useEffect(() => {
    if (user?.id) {
      getUserCart();
    }
  }, [user?.id]);

  const inStock = vendorVariant.availability === 'in_stock' || vendorVariant.availability === 'back_ordered';

  const isSalePrice = currentPrice && retailPrice?.price && currentPrice < retailPrice?.price;

  const [imageLoaded, setImageLoaded] = useState(false);

  const [addToCart, { buttonLabel }] = useAddToCart({
    location: 'design board',
    vendorVariant
  });

  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef?.current?.complete) {
      setImageLoaded(true);
    }
  }, []);

  const aspectRatio = 1.042;

  const onLoad = () => {
    setImageLoaded(true);
  };

  const onError = () => {
    setImageLoaded(true);
  };

  return (
    <figure className={ `${className} ${theme.styles.Body}` }>
      <a
        className={ styles.ImageButton }
        href={ productUrl }
        onClick={ (e) => handleClick(e, setQuickViewModalContent, vendorVariant) }
      >
        <If condition={isOnSale && inStock}>
          <SaleBanner
            className={styles.SaleBanner}
            backgroundColor={theme.colors.shade6}
            product={vendorVariant}
          />
        </If>

        { !!designAward && (
          <DesignAwardBadge
            place={designAward}
            className={styles.DesignAwardBadge}
          />
        )}

        <AspectRatioBox
          aspectRatio={aspectRatio}
          className={ styles.AspectRatioBox }
        >
          <Image
            alt={ `${title} - ${vendor.name}` }
            className={ `${styles.Image} ${imageLoaded ? styles.isLoaded : ''}` }
            src={ image.uri }
            layout="fill"
            sizes={`(min-width: ${breakpoints.desktop}px) 25vw,
                  (min-width: ${breakpoints['medium-tablet']}px),
                  45vw`}
            onLoad={ onLoad }
            onError={ onError }
          />

          <If condition={ !imageLoaded }>
            <Skeleton />
          </If>

        </AspectRatioBox>

      </a>
      <If condition={ !readOnly }>
        <div className={ styles.SaveButtonPositionTrick }>
          <SaveProductForLater
            handleSave={() => onSave(vendorVariant)}
            className={styles.SaveButton}
            saved={ saved }
          />
        </div>
      </If>
      <figcaption className={ styles.ProductDetails }>
        <Variations
          className={styles.Variations}
          productGroupUrl={productGroupUrl}
          count={groupCount}
        />
        <span className={classnames(theme.styles.Body, styles.ProductDetailsLine)}>
          {vendor.name}
        </span>
        <span className={classnames(theme.styles.Body, styles.ProductTitle)}>
          {vendorVariant.title}
        </span>
        <If condition={inStock}>
          <span className={classnames(
            theme.styles.Body,
            styles.ProductDetailsLinePrice,
            `${isSalePrice ? styles.SalePrice : ''}`,
          )}
          >
            {formatCost(currentPrice)}
          </span>
          <If condition={isSalePrice}>
            <span className={classnames(styles.ProductDetailsLinePrice, styles.Strikethrough)}>
              {formatCost(retailPrice?.price)}
            </span>
          </If>
          <If condition={vendorVariant.availability === 'back_ordered'}>
            <span className={
              classnames(
                theme.styles.Body,
                styles.ProductDetailsOutStock
              )
            }
            >
              Back ordered
            </span>
          </If>
        </If>
        <If condition={!inStock}>
          <span className={
            classnames(
              theme.styles.Body,
              styles.ProductDetailsOutStock
            )
          }
          >
            Out of stock
          </span>
        </If>
      </figcaption>

      <button
        type={'button'}
        className={classnames(
          className,
          theme.styles.Button,
          theme.styles.Transparent,
          theme.styles.Small,
          { [theme.styles.Disabled]: buttonLabel === ADDED_TO_CART },
          styles.AddCartButton,
        )}
        onClick={() => addToCart()}
        disabled={buttonLabel === ADDED_TO_CART}
      >
        {buttonLabel}
      </button>
    </figure>
  );
}

function handleClick(e, setQuickViewModalContent, vendorVariant) {
  e.preventDefault();
  if (setQuickViewModalContent && typeof setQuickViewModalContent === 'function') {
    setQuickViewModalContent(vendorVariant);
  }
  handleTrackExploreProductClicked(vendorVariant);
}

function handleTrackExploreProductClicked(vendorVariant) {
  const {
    image, retailPrice, currentPrice, title, vendor, id, isOnSale
  } = vendorVariant;

  analyticsTrack(
    'explore_product_clicked',
    {
      clickType: 'explore-view-pdp',
      clickedFrom: '/interior-design-ideas/boards',
      imageUrl: image.uri || '',
      price: retailPrice?.price || '',
      productName: title,
      salePrice: isOnSale ? currentPrice : '',
      vendorName: vendor.name,
      vendorVariantId: id,
    },
  );
}
